import { useEffect, useState } from "react";
import GuideContentArea from "../../../core/common/GuideContentArea";
import { all_routes } from "../../router/all_routes";
import { PageBreadcrumb } from "../../../core/common/BreadCrumbForm";
import Datatable from "../../../core/common/dataTable";
import { cellWidth } from "../../../core/methods/tableCellWidth";
import Footer from "../../../core/common/Footer";
import CheckListTable from "./CheckListTable";
import useApi from "../../../core/customhook/useApi";
import { useLocation } from "react-router";
import dayjs from "dayjs";
import { SearchPayloadType } from "../FiltersTrackCase";
import SnipperLoader from "../../../core/common/loader/SnipperLoader";

const guideData = [
  "Search for the candidates package by candidate name or case ID",
  "The candidates existing package will show up with the current status of the existing checks and key value (company name/university name/address)",
  "Click on the Add Check Option - Select the check and quantity to update the existing package with the new checks.",
  "You can either fill in the information or you can choose to send this to the candidate for filling.",
  "If you choose to send this to the applicant - once the applicant has filled in the requested information we will automatically add it to the existing package and start the background check process",
  "The turn around time will be as per the SLA terms of the agreement",
];

const breadcrumbItem = [
  { label: "Track Case Program", path: all_routes.trackCase },
  { label: "Case Details", path: all_routes.caseDetails },
  { label: "Add Case" },
];

const AddCheck = () => {
  const location = useLocation();

  const assignment_id = location.state?.assignment_id;
  const [showGuide, setShowGuide] = useState(false);
  const [showCheckData, setShowCheckData] = useState(false);
  const [lastSearchPayload, setLastSearchPayload] = useState<
    Partial<SearchPayloadType>
  >({});
  const [checkHistory, setCheckHistory] = useState<any[]>([]);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
  });

  const [total, setTotal] = useState(0);
  const getChecksHistoryAPI = useApi<any>(
    assignment_id
      ? `/api/v1/candidate/forms/case-check-summary/${assignment_id}`
      : "",
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const fetchCheckHistory = async (
    searchPayload: Partial<SearchPayloadType> = {},
    page: number = pagination.page,
    limit: number = pagination.limit,
  ) => {
    if (!assignment_id) return;

    try {
      const res = await getChecksHistoryAPI.callApi();
      const historyList = Array.isArray(res?.data?.checks)
        ? res.data.checks
        : [];

      setCheckHistory(historyList);
      setTotal(historyList.length);
      setLastSearchPayload(searchPayload);
    } catch (error) {
      console.error(error);
      setCheckHistory([]);
      setTotal(0);
    }
  };

  useEffect(() => {
    if (assignment_id) {
      fetchCheckHistory();
    }
  }, [assignment_id]);

  const handleTableChange = (
    page: number,
    limit: number = pagination.limit,
  ) => {
    setPagination({ page, limit });
    fetchCheckHistory(lastSearchPayload, page, limit);
  };

  const columns = [
    {
      title: "Sr. No.",
      key: "srNo",
      width: 80,
      align: "center",
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Check Type",
      dataIndex: "check_name",
      key: "check_name",
      onCell: () => cellWidth(240),
    }, 
    {
      title: "Check Status",
      dataIndex: "status",
      key: "status",
      onCell: () => cellWidth(180),
    },
    {
      title: "SLA Start Date",
      dataIndex: "start_date",
      key: "start_date",
      align: "center",
      render: (value: string) =>
        value ? dayjs(value).format("DD-MM-YYYY") : "-",
      onCell: () => cellWidth(140),
    },
  ];

  const loader = getChecksHistoryAPI.loading;

  return (
    <>
      <style>{`
        .ac-card {
          border: 1px solid #eef0f3;
          border-radius: 14px;
          box-shadow: 0 2px 10px rgba(20, 20, 43, 0.04);
          overflow: hidden;
          transition: box-shadow 0.2s ease;
        }
        .ac-card:hover {
          box-shadow: 0 6px 18px rgba(20, 20, 43, 0.07);
        }
        .ac-card-header {
          background: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%);
          border-bottom: 1px solid #eef0f3;
        }
        .ac-section-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #4338ca;
          margin-right: 10px;
          flex-shrink: 0;
        }
        .ac-add-check-btn {
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.9rem;
          padding: 0.55rem 1.25rem;
          box-shadow: 0 2px 8px rgba(67, 56, 202, 0.18);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
          border: none;
        }
        .ac-add-check-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(67, 56, 202, 0.25);
        }
        .ac-add-check-btn i {
          font-size: 1.1rem;
          vertical-align: -2px;
          margin-right: 4px;
        }
        .ac-table-wrapper table {
          font-size: 0.9rem;
        }
      `}</style>

      <div className="page-wrapper">
        <div className="content px-2 px-md-4 pb-5">
          <PageBreadcrumb
            title="Add Check"
            breadcrumbs={breadcrumbItem}
            showBackButton={true}
            backRoute={all_routes.caseDetails}
            showGuideButton={true}
            onGuideClick={() => setShowGuide((prev) => !prev)}
          />
          <>
            <GuideContentArea
              showGuide={showGuide}
              handleClick={() => setShowGuide(false)}
              title={"Add Check"}
              content={
                <div>
                  <p>
                    To add a check to a candidates existing package follow the
                    instructions below:-
                  </p>
                  <ul>
                    {guideData?.map((i, idx) => (
                      <li key={idx} className="pb-2">
                        {idx + 1}. {i}
                      </li>
                    ))}
                  </ul>
                </div>
              }
            />
          </>

          <div className="mt-4">
            <div className="card ac-card my-4">
              <div className="ac-card-header d-flex align-items-center px-3 py-3">
                <span className="ac-section-badge"></span>
                <span className="fw-semibold text-dark">Case Overview</span>
              </div>

              {/* Table  */}
              {loader ? (
                <div className="py-4">
                  <SnipperLoader />
                </div>
              ) : (
                <div className="ac-table-wrapper px-4 pb-2 pt-4">
                  <Datatable
                    dataSource={checkHistory}
                    columns={columns}
                    Selection={false}
                    hideSizeChanger={true}
                    pagination={{
                      current: pagination.page,
                      pageSize: pagination.limit,
                      total,
                    }}
                    handleTableChange={(pagination: any) => {
                      handleTableChange(
                        pagination.current,
                        pagination.pageSize,
                      );
                    }}
                  />
                </div>
              )}
            </div>

            <div className="d-flex justify-content-start mb-2">
              <button
                type="button"
                className="btn btn-primary ac-add-check-btn d-inline-flex align-items-center"
                onClick={() => setShowCheckData(true)}
              >
                <i className="bi bi-plus"></i>Show Checks
              </button>
            </div>

            {showCheckData && (
              <CheckListTable
                assignmentID={assignment_id}
                onSuccess={fetchCheckHistory}
              />
            )}
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default AddCheck;
